import {
  type UpdateUserProfileInput,
  type useEditSidebarUpdateUserProfileMutation,
} from '#app/__generated__/relay/useEditSidebarUpdateUserProfileMutation.graphql.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useContraMutation } from '#app/hooks/useContraMutation.js';
import { graphql } from '@contra/relay';
import { captureException } from '@contra/sentry/react';
import { useCallback } from 'react';

export const useEditSidebar = () => {
  const toast = useToast();
  const [updateProfile] =
    useContraMutation<useEditSidebarUpdateUserProfileMutation>(graphql`
      mutation useEditSidebarUpdateUserProfileMutation(
        $input: UpdateUserProfileInput!
      ) {
        updateUserProfile(input: $input) {
          errors {
            __typename
            message
          }

          userAccount {
            ...OpenProfileCompletionChecklistButton_userAccount
            id
            userGroups
            profile {
              locationGooglePlaceId
              location
              firstName
              lastName
              bio
              ...SkillsAndTools_userProfile
            }
          }
        }
      }
    `);

  const updateBio = useCallback(
    (bio: UpdateUserProfileInput['bio'], onCompleted: () => void) => {
      updateProfile({
        onCompleted: (errors) => {
          const { apiErrors, hasErrors, payloadErrors } = errors;
          if (hasErrors) {
            if (payloadErrors) {
              toast.error('An unexpected error occurred. Please try again.');
              captureException(payloadErrors[0]);
            } else if (apiErrors) {
              toast.error(apiErrors[0].message);
            }
          } else {
            onCompleted();
          }
        },
        onError: (error) => {
          captureException(error);
        },
        variables: {
          input: {
            bio,
          },
        },
      });
    },
    [toast, updateProfile],
  );

  const updateSkills = useCallback(
    (skills: UpdateUserProfileInput['roles'], onCompleted: () => void) => {
      updateProfile({
        onCompleted: (errors) => {
          const { apiErrors, hasErrors, payloadErrors } = errors;
          if (hasErrors) {
            if (payloadErrors) {
              toast.error('An unexpected error occurred. Please try again.');
              captureException(payloadErrors[0]);
            } else if (apiErrors) {
              toast.error(apiErrors[0].message);
            }
          } else {
            onCompleted();
          }
        },
        onError: (error) => {
          captureException(error);
        },
        variables: {
          input: {
            roles: skills,
          },
        },
      });
    },
    [toast, updateProfile],
  );

  const updateLocation = useCallback(
    (
      locationGooglePlaceId: UpdateUserProfileInput['locationGooglePlaceId'],
      onCompleted: () => void,
    ) => {
      updateProfile({
        onCompleted: (errors) => {
          const { apiErrors, hasErrors, payloadErrors } = errors;
          if (hasErrors) {
            if (payloadErrors) {
              toast.error('An unexpected error occurred. Please try again.');
              captureException(payloadErrors[0]);
            } else if (apiErrors) {
              toast.error(apiErrors[0].message);
            }
          } else {
            onCompleted();
          }
        },
        onError: (error) => {
          captureException(error);
        },
        variables: {
          input: {
            locationGooglePlaceId,
          },
        },
      });
    },
    [toast, updateProfile],
  );

  const updateFullName = useCallback(
    (
      {
        firstName,
        lastName,
      }: {
        firstName: UpdateUserProfileInput['firstName'];
        lastName: UpdateUserProfileInput['lastName'];
      },
      onCompleted: () => void,
    ) => {
      updateProfile({
        onCompleted: () => {
          onCompleted();
        },
        onError: (error) => {
          captureException(error);
        },
        variables: {
          input: {
            firstName,
            lastName,
          },
        },
      });
    },
    [updateProfile],
  );

  return {
    updateBio,
    updateFullName,
    updateLocation,
    updateSkills,
  };
};
